import {
  Component, Inject, ChangeDetectionStrategy, HostListener, Input,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { IS_BROWSER, IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { HotelDetailResponse, HotelAmenity } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { MapMarkerInputs } from '@shared/map/constants/map.constants';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SearchType } from '@search-bar/entities/search-type';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { getAmmenities } from '@app/mappers/ammenities.mapper';
import { ModalGalleryComponent } from '@ux/components/image-gallery/modal-gallery/modal-gallery.component';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-hotel-detail-desktop',
  templateUrl: './hotel-detail-desktop.component.html',
  styleUrls: ['./hotel-detail-desktop.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelDetailDesktopComponent {
  
  @Input() hasRappiCard: boolean;
  @Input() isPrime: boolean;
  @Input() isLoggedIn: boolean;

  @ViewChild(ModalGalleryComponent) modalGallery: ModalGalleryComponent;

  public showMap: boolean = false;
  public showGallery: boolean = false;
  public detailSticky: boolean = false;
  public searchType: SearchType = SearchType.HOTELS_DETAIL;
  public source: EVENT_SOURCE | string = EVENT_SOURCE.DETAIL;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  private heigthScrollSticky: number = 181;

  constructor(
    private facade: HotelDetailFacade,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private headerService: HeaderService,
    private domManipuleService: DomManipulateService,
    protected route: ActivatedRoute,
    @Inject(IS_BROWSER) public isBrowser: boolean,
    @Inject(IS_MOBILE) public isMobile: boolean
  ) { }


  @HostListener('window:scroll', ['$event']) onWindowScroll(event: Event): void {
    const offset = window['pageYOffset'] || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.detailSticky = offset >= this.heigthScrollSticky;
  }

  get isPackageDetail$(): Observable<boolean> {
    return this.baseSearchBarFacade.packageStep$.pipe(map(packageStep =>
      !!packageStep && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL));
  }

  get params$(): Observable<Params> {
    return this.route.params;
  }

  get hotelAmmenities(): HotelAmenity[] {
    return getAmmenities() as HotelAmenity[];
  }

  get hotelDetail$(): Observable<HotelDetailResponse> | null {
    return this.facade.hotelDetail$.pipe(map(detail => detail.Info || detail.Result ? detail : null));
  }

  get isLoadingHotelDetail$(): Observable<boolean> {
    return this.facade.isLoadingHotelDetail$;
  }

  get hotelMarkers$(): Observable<MapMarkerInputs[]> {
    return this.facade.hotelMarkers$;
  }

  get hotelAddress$(): Observable<string> {
    return this.hotelDetail$.pipe(
      map(hotelDetail => hotelDetail?.Info?.FullAddress || hotelDetail?.Info?.Address)
    );
  }

  public toggleMap(): void {
    this.showMap = !this.showMap;
    this.domManipuleService.setBodyScroll(!this.showMap);
  }

  public back(): void {
    this.headerService.goBack();
  }

  public toggleShowGallery(): void {
    this.modalGallery.toggleShowGallery();
  }

}
